import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Share2, Check } from 'lucide-react';

// Modal that displays a large QR code for the share URL.
// Designed to be readable across a classroom.
export default function QRShareModal({ open, url, title, subtitle, caption, onClose }) {
  const [copied, setCopied] = useState(false);

  // ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset copied state when reopened
  useEffect(() => { if (open) setCopied(false); }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }
    } catch { /* ignore */ }
  };

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: title || 'Metronomo', url });
      }
    } catch { /* user cancelled or unsupported */ }
  };

  return (
    <div
      data-testid="qr-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md bg-black/85 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-[#111112] border border-pink-400/30 rounded-2xl shadow-[0_0_60px_rgba(236,72,153,0.25)] w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-pink-300/80">
              Condividi via QR
            </div>
            <div className="font-bebas text-2xl tracking-wide text-white truncate max-w-[18rem]">
              {title || 'Metronomo'}
            </div>
          </div>
          <button
            data-testid="qr-modal-close"
            onClick={onClose}
            className="text-white/50 hover:text-white p-1.5 rounded transition-all"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center gap-4">
          {/* QR code on white background — required for camera readability */}
          <div className="bg-white p-4 rounded-xl shadow-2xl">
            <QRCodeSVG
              data-testid="qr-code-svg"
              value={url}
              size={256}
              level="M"
              marginSize={0}
              fgColor="#0A0A0A"
              bgColor="#FFFFFF"
            />
          </div>

          {subtitle && (
            <div className="text-xs text-white/60 text-center font-mono">
              {subtitle}
            </div>
          )}

          <div className="text-xs text-pink-300/70 text-center uppercase tracking-widest">
            {caption || "Scansiona per aprire l'esercizio"}
          </div>

          {/* URL display */}
          <div data-testid="qr-url-display" className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 font-mono text-[11px] text-white/60 truncate" title={url}>
            {url}
          </div>

          <div className="flex gap-2 w-full">
            <button
              data-testid="qr-copy-btn"
              onClick={handleCopy}
              className={`flex-1 h-11 rounded-md flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-widest active:scale-95 transition-all ${
                copied
                  ? 'bg-emerald-400 text-black'
                  : 'bg-pink-400 hover:bg-pink-300 text-black'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" /> Copiato
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copia link
                </>
              )}
            </button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                data-testid="qr-native-share-btn"
                onClick={handleNativeShare}
                className="h-11 px-4 rounded-md border border-white/15 hover:border-pink-400/50 text-white text-xs font-medium uppercase tracking-widest flex items-center gap-2 active:scale-95 transition-all"
              >
                <Share2 className="w-4 h-4" /> Sistema
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
